<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import CarouselItem from "@/components/carousel/CarouselItem.vue";
import CarouselControls from "@/components/carousel/CarouselControls.vue";
import { fetchNews } from "@/api/news";
import { NewsItem } from "@/types/news";

const carouselRef = ref<HTMLElement | null>(null);
const items = ref<NewsItem[]>([]);
const currentIndex = ref(0);
const scrollDelta = ref(0);
const isScrollDown = ref<boolean>();

const currentItem = computed(() => items.value[currentIndex.value]);

let intervalId: ReturnType<typeof setInterval> | null = null;
const scrollThreshold = 200;
let waitScrollDelay = false;

function startInterval() {
  if (intervalId) clearInterval(intervalId);
  intervalId = setInterval(nextSlideCycled, 5000);
}

const nextSlideCycled = () => {
  currentIndex.value = (currentIndex.value + 1) % items.value.length;
};

function prevSlide() {
  if (currentIndex.value > 0) {
    currentIndex.value--;
    startInterval();
  }
}

function nextSlide() {
  if (currentIndex.value < items.value.length - 1) {
    currentIndex.value++;
    startInterval();
  }
}

function goToSlide(index: number) {
  currentIndex.value = index;
  startInterval();
}

onMounted(async () => {
  items.value = await fetchNews();
  startInterval();
  if (carouselRef.value) {
    console.log("set scroll listener");
    carouselRef.value.addEventListener("wheel", handleWheel, {
      passive: false,
    });
  } else {
    console.log("NOT set scroll listener");
  }
});

onBeforeUnmount(() => {
  if (intervalId) clearInterval(intervalId);
  if (carouselRef.value) {
    carouselRef.value.removeEventListener("wheel", handleWheel);
  }
});

function handleWheel(e: WheelEvent) {
  e.preventDefault();
  if (waitScrollDelay) {
    return;
  }

  const isNowScrollDown = e.deltaY < 0;
  if (isNowScrollDown !== isScrollDown.value) {
    scrollDelta.value = 0;
  }
  isScrollDown.value = isNowScrollDown;
  scrollDelta.value += e.deltaY;

  if (Math.abs(scrollDelta.value) >= scrollThreshold) {
    if (isScrollDown.value) {
      prevSlide();
    } else {
      nextSlide();
    }
    scrollDelta.value = 0;
    waitScrollDelay = true;
    setTimeout(() => (waitScrollDelay = false), 300);
  }
}
</script>

<template>
  <div ref="carouselRef" class="flex flex-col relative h-full overflow-hidden">
    <div class="absolute w-full h-full" v-if="currentItem">
      <CarouselItem :news="currentItem" />
    </div>

    <div
      class="absolute left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 bottom-6 md:right-6 md:bottom-10 xl:right-[72px] xl:bottom-[72px]"
    >
      <CarouselControls
        :currentIndex="currentIndex"
        :total="items.length"
        @prev="prevSlide"
        @next="nextSlide"
        @select="goToSlide"
      />
    </div>
  </div>
</template>
